import { ref, nextTick } from 'vue'
import toast from '@/plugins/toasts'
import eventbus from '@/plugins/eventbus'
import importProgressWS from '@/services/websocket/import-progress'
import store from '@/store'
import importsAPI from '@/api/imports'

function isOnBookmarksPage() {
  // Extension uses hash routing, web app uses history routing
  const isExtension = BUILD_TARGET === 'extension'
  const path = isExtension ? window.location.hash.slice(1) : window.location.pathname

  // Check the actual route path - works for both web app and extension popup
  // The bookmarks tab is at "/bookmarks" route in both environments
  return path === '/bookmarks' || path === '/' || path === ''
}

/**
 * Check if an import ID is a fake/client-side ID that should not be sent to the backend
 * @param {string} importId - The import ID to check
 * @returns {boolean} True if the ID is fake (client-side only), false if it's a real UUID
 */
function isFakeImportId(importId) {
  if (!importId) return true
  // All fake IDs are prefixed patterns, real UUIDs from backend never have these prefixes
  // sync- : Created for synchronous imports that complete immediately
  // polling- : Created when WebSocket fails and polling starts
  // import- : Created on confirmation page for UI consistency
  return importId.startsWith('sync-') ||
         importId.startsWith('polling-') ||
         importId.startsWith('import-')
}

// Global import toast manager
class ImportToastManager {
  constructor() {
    this.isImportToastOpen = ref(false)
    this.importToastInstance = ref(null)
    this.importToastProps = ref({})
    this.importToastTimer = ref(null)
    this.initialized = false
    this.pendingImport = null
    this.pollingInterval = null
    this.minimumBookmarksForToast = 1
    this.hasSessionProgress = false
    this.importSuppressed = false
    this.cleanupFunctions = []
    this.currentUserId = null
    this.currentSessionImportId = null
    this.recentlyCompletedImportIds = new Set()
    this.activeImports = new Map()
    this.completedStats = { total: 0, processed: 0, failed: 0 }
    this.maxPercentageSeen = 0
  }

  initialize() {
    if (this.initialized) return

    this.currentUserId = store.getters['auth/userId']

    // Listen to import events globally
    const handlers = {
      'import:started': this.handleImportStarted.bind(this),
      'import:progress': this.handleImportProgress.bind(this),
      'import:completed': this.handleImportCompleted.bind(this),
      auth: this.handleUserLogin.bind(this)
    }

    Object.entries(handlers).forEach(([event, handler]) => {
      eventbus.on(event, handler)
      this.cleanupFunctions.push(() => eventbus.off(event, handler))
    })

    // Listen to window logout event
    const logoutHandler = this.handleUserLogout.bind(this)
    window.addEventListener('rubra:auth:logout', logoutHandler)
    this.cleanupFunctions.push(() =>
      window.removeEventListener('rubra:auth:logout', logoutHandler)
    )

    this.initialized = true
  }

  handleImportStarted({ importId, totalBookmarks }) {
    // Only show toast for non-empty imports
    if (totalBookmarks < this.minimumBookmarksForToast) {
      return
    }

    const importData = {
      importId,
      totalBookmarks,
      bookmarksProcessed: 0,
      bookmarksFailed: 0,
      attempted: 0,
      pending: totalBookmarks,
      status: 'processing',
      startedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString()
    }

    // Track in active imports map for multi-import aggregation
    this.activeImports.set(importId, importData)

    this.pendingImport = importData
    this.currentSessionImportId = importId
    this.hasSessionProgress = true
    this.persistImport(importData)

    if (importId && !importId.startsWith('sync-')) {
      store.commit('collectionItems/setActiveImport', importData)
    }

    // Initialize toast props — use aggregate when multiple imports active
    const activeCount = this.getActiveImportCount()
    if (activeCount > 1) {
      const agg = this.getDisplayProgress()
      this.importToastProps.value = {
        showPercentage: true,
        showProgressCounter: CONFIG?.features?.showImportProgressCounter ?? true,
        isRunning: true,
        importQueueSize: agg.pending,
        lastImportSize: agg.total,
        progressPercentage: this.enforceMonotonicProgress(agg.percentage),
        status: 'processing',
        totalBookmarks: agg.total,
        bookmarksProcessed: agg.processed,
        bookmarksFailed: agg.failed,
        attempted: agg.attempted,
        totalAttempted: agg.attempted,
        totalQueueSize: agg.pending,
        lastUpdated: new Date(),
        activeImportCount: activeCount
      }
    } else {
      this.maxPercentageSeen = 0
      this.importToastProps.value = {
        showPercentage: true,
        showProgressCounter: CONFIG?.features?.showImportProgressCounter ?? true,
        isRunning: true,
        importQueueSize: totalBookmarks || 0,
        lastImportSize: totalBookmarks || 0,
        progressPercentage: 0,
        status: 'processing',
        totalBookmarks: totalBookmarks || 0,
        bookmarksProcessed: 0,
        bookmarksFailed: 0,
        attempted: 0,
        totalAttempted: 0,
        totalQueueSize: totalBookmarks || 0,
        lastUpdated: new Date(),
        activeImportCount: 1
      }
    }

    // Try to track with WebSocket if available
    if (importProgressWS && importId && !importId.startsWith('sync-')) {
      importProgressWS.trackImport(importId, { totalBookmarks }).catch((error) => {
        console.error('[ImportToastManager] Failed to track import with WebSocket:', error)
        this.handleWebSocketFailure(importId)
        // Immediately start polling as fallback
        if (!this.pollingInterval) {
          this.startProgressPolling()
        }
      })

      const errorHandler = (event) => {
        if (event && event.importId === importId) {
          this.handleImportError(event.error || new Error('WebSocket error'), importId)
          // Start polling on WebSocket error
          if (!this.pollingInterval) {
            console.warn('[ImportToastManager] Starting polling due to WebSocket error')
            this.startProgressPolling()
          }
        }
      }
      eventbus.$once('import:ws:error', errorHandler)

      // Safety timeout: if WebSocket doesn't connect within 3 seconds, start polling
      setTimeout(() => {
        if (!importProgressWS.state.connected && !this.pollingInterval) {
          console.warn('[ImportToastManager] WebSocket not connected after 3s, starting polling fallback')
          this.startProgressPolling()
        }
      }, 3000)
    } else {
      this.startProgressPolling()
    }

    // Only show toast if we're on the bookmarks page
    if (isOnBookmarksPage()) {
      this.showImportToast()
    }
  }

  handleImportProgress({ importId, progress, totalQueueSize, totalAttempted }) {
    const normalized = this.normalizeProgress(progress)

    if (normalized.attempted > 0) {
      this.hasSessionProgress = true
    }

    // Auto-track imports we receive progress for (e.g., after page reload)
    if (!this.activeImports.has(importId)) {
      // Don't auto-track imports that were recently completed — prevents zombie entries
      // from late-arriving progress events after completion
      if (this.recentlyCompletedImportIds.has(importId)) {
        return
      }
      this.activeImports.set(importId, {
        importId,
        totalBookmarks: normalized.total,
        bookmarksProcessed: normalized.processed,
        bookmarksFailed: normalized.failed,
        attempted: normalized.attempted,
        pending: normalized.pending,
        status: normalized.status,
        lastUpdated: normalized.lastUpdated
      })
    } else {
      this.activeImports.set(importId, {
        ...this.activeImports.get(importId),
        status: normalized.status,
        totalBookmarks: normalized.total,
        bookmarksProcessed: normalized.processed,
        bookmarksFailed: normalized.failed,
        attempted: normalized.attempted,
        pending: normalized.pending,
        lastUpdated: normalized.lastUpdated,
        lastError: normalized.lastError
      })
    }

    if (this.pendingImport && this.pendingImport.importId === importId) {
      this.pendingImport = {
        ...this.pendingImport,
        status: normalized.status,
        totalBookmarks: normalized.total,
        bookmarksProcessed: normalized.processed,
        bookmarksFailed: normalized.failed,
        lastUpdated: normalized.lastUpdated.toISOString(),
        lastError: normalized.lastError
      }
    }

    // Use aggregate display when multiple imports are active or partially completed
    const activeCount = this.getActiveImportCount()
    const hasCompletedInSession = this.completedStats.total > 0

    if (activeCount > 1 || hasCompletedInSession) {
      const agg = this.getDisplayProgress()
      this.importToastProps.value = {
        showPercentage: true,
        showProgressCounter: CONFIG?.features?.showImportProgressCounter ?? true,
        isRunning: true,
        progressPercentage: this.enforceMonotonicProgress(agg.percentage),
        status: 'processing',
        totalBookmarks: agg.total,
        bookmarksProcessed: agg.processed,
        bookmarksFailed: agg.failed,
        importQueueSize: agg.pending,
        lastImportSize: agg.total,
        attempted: agg.attempted,
        totalAttempted: agg.attempted,
        totalQueueSize: agg.pending,
        lastUpdated: normalized.lastUpdated,
        activeImportCount: activeCount
      }
    } else {
      this.importToastProps.value = {
        showPercentage: true,
        showProgressCounter: CONFIG?.features?.showImportProgressCounter ?? true,
        isRunning: ['processing', 'pending'].includes(normalized.status),
        progressPercentage: this.enforceMonotonicProgress(normalized.progressPercentage),
        status: normalized.status,
        totalBookmarks: normalized.total,
        bookmarksProcessed: normalized.processed,
        bookmarksFailed: normalized.failed,
        importQueueSize: normalized.pending,
        lastImportSize: normalized.total,
        attempted: normalized.attempted,
        totalAttempted: totalAttempted ?? normalized.attempted,
        totalQueueSize: totalQueueSize ?? normalized.pending,
        lastUpdated: normalized.lastUpdated,
        activeImportCount: 1
      }
    }

    if (!this.isImportToastOpen.value) {
      this.showImportToast()
    }
  }

  handleImportCompleted(event) {
    const { importId } = event

    // Prevent duplicate completion toasts for the same import
    if (this.recentlyCompletedImportIds.has(importId)) {
      this.activeImports.delete(importId)
      return
    }

    const progressPayload = event.progress || event
    const normalized = this.normalizeProgress({
      ...progressPayload,
      status: event.status || progressPayload.status || 'completed',
      totalBookmarks: event.totalBookmarks ?? progressPayload.totalBookmarks,
      bookmarksProcessed: event.bookmarksProcessed ?? progressPayload.bookmarksProcessed,
      bookmarksFailed: event.bookmarksFailed ?? progressPayload.bookmarksFailed,
      lastError: event.error ?? progressPayload.lastError
    })

    if (normalized.attempted > 0) {
      this.hasSessionProgress = true
    }

    // Only accumulate stats for imports we're actually tracking
    const isTrackedImport = this.activeImports.has(importId) || this.pendingImport?.importId === importId
    if (!isTrackedImport) {
      // Stale or unknown import — clean up localStorage if it matches, but don't accumulate stats
      const stored = this.getStoredImport()
      if (stored?.importId === importId) {
        this.clearPersistedImport()
      }
      return
    }

    // Accumulate completed stats for aggregate display
    this.completedStats.total += normalized.total
    this.completedStats.processed += normalized.processed
    this.completedStats.failed += normalized.failed

    // Remove from active imports
    this.activeImports.delete(importId)
    this.recentlyCompletedImportIds.add(importId)

    // Check if there are still active imports running
    const remainingActive = this.getActiveImportCount()

    if (remainingActive > 0) {
      // Still have running imports — update toast with combined progress and continue
      const agg = this.getDisplayProgress()
      this.importToastProps.value = {
        ...this.importToastProps.value,
        isRunning: true,
        progressPercentage: this.enforceMonotonicProgress(agg.percentage),
        status: 'processing',
        totalBookmarks: agg.total,
        bookmarksProcessed: agg.processed,
        bookmarksFailed: agg.failed,
        importQueueSize: agg.pending,
        attempted: agg.attempted,
        totalAttempted: agg.attempted,
        totalQueueSize: agg.pending,
        activeImportCount: remainingActive,
        lastUpdated: new Date()
      }

      // Switch pendingImport to next active import
      const nextActive = [...this.activeImports.values()].find(
        imp => !['completed', 'failed', 'timeout', 'cancelled'].includes(imp.status)
      )
      if (nextActive) {
        this.pendingImport = {
          importId: nextActive.importId,
          totalBookmarks: nextActive.totalBookmarks || nextActive.total,
          bookmarksProcessed: nextActive.bookmarksProcessed || nextActive.processed || 0,
          bookmarksFailed: nextActive.bookmarksFailed || nextActive.failed || 0,
          status: nextActive.status || 'processing',
          startedAt: nextActive.startedAt || new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        }
        this.currentSessionImportId = nextActive.importId
        this.persistImport(this.pendingImport)
      }

      return
    }

    // === All imports completed ===

    // Use accumulated stats for final display
    const finalTotal = this.completedStats.total
    const finalProcessed = this.completedStats.processed
    const finalFailed = this.completedStats.failed
    const finalStatus = normalized.status
    const finalPercentage = finalStatus === 'completed' ? 100
      : (finalTotal > 0 ? Math.round(((finalProcessed + finalFailed) / finalTotal) * 100) : 0)

    // Reset completed stats and monotonic tracker for next import cycle
    this.completedStats = { total: 0, processed: 0, failed: 0 }
    this.maxPercentageSeen = 0

    // Only show completion toast if we had session progress
    if (!this.hasSessionProgress && normalized.status === 'timeout') {
      this.completedStats = { total: 0, processed: 0, failed: 0 }
      this.pendingImport = null
      this.isImportToastOpen.value = false
      this.importToastInstance.value = null
      store.commit('collectionItems/clearActiveImport')
      return
    }

    // Update pending import with completion info
    if (this.pendingImport) {
      this.pendingImport = {
        ...this.pendingImport,
        status: finalStatus,
        totalBookmarks: finalTotal,
        bookmarksProcessed: finalProcessed,
        bookmarksFailed: finalFailed,
        completedAt: new Date().toISOString(),
        lastUpdated: normalized.lastUpdated.toISOString(),
        lastError: normalized.lastError
      }
      this.persistImport(this.pendingImport)
    }

    this.importToastProps.value = {
      ...this.importToastProps.value,
      isRunning: false,
      importQueueSize: 0,
      progressPercentage: finalPercentage,
      status: finalStatus,
      totalBookmarks: finalTotal,
      bookmarksProcessed: finalProcessed,
      bookmarksFailed: finalFailed,
      attempted: finalProcessed + finalFailed,
      error: event.error || normalized.lastError,
      lastUpdated: normalized.lastUpdated,
      activeImportCount: 0
    }

    // Show completion toast if we're on the bookmarks page
    if (isOnBookmarksPage() && !this.isImportToastOpen.value) {
      this.showImportToast()
    }

    const shouldClearPersisted = isOnBookmarksPage()

    // Clear pending import after a delay - gives user time to see completion toast
    setTimeout(() => {
      // Only cleanup if no new imports started during the delay
      if (this.getActiveImportCount() === 0) {
        this.pendingImport = null
        this.isImportToastOpen.value = false
        this.importToastInstance.value = null
        this.recentlyCompletedImportIds.clear()
        store.commit('collectionItems/clearActiveImport')

        if (shouldClearPersisted) {
          this.clearPersistedImport()
        }
      }
    }, 10000)
  }

  showImportToast() {
    if (!isOnBookmarksPage()) {
      return
    }

    if (this.isImportToastOpen.value) {
      return
    }

    this.isImportToastOpen.value = true

    // Clear any existing timer
    if (this.importToastTimer.value) {
      clearTimeout(this.importToastTimer.value)
    }

    // Show toast immediately
    nextTick(() => {
      if (toast && toast.open) {
        const toastOptions = {
          component: 'BookmarkImport',
          componentProps: () => this.importToastProps.value,
          listeners: {
            close: () => {
              this.isImportToastOpen.value = false
              this.importToastInstance.value = null
            }
          },
          props: {
            color: 'blue25 text-neutral900 d-block',
            custom: true,
            timeout: 900e3
          },
          onClose: () => {
            this.isImportToastOpen.value = false
            this.importToastInstance.value = null
          }
        }

        try {
          this.importToastInstance.value = toast.open(toastOptions)
        } catch (error) {
          console.error('[ImportToastManager] Error opening toast:', error)
        }
      } else {
        console.error('[ImportToastManager] Toast plugin not available')
      }

      // Fallback timer to reset flag
      this.importToastTimer.value = setTimeout(() => {
        this.isImportToastOpen.value = false
      }, 900e3 + 5000)
    })
  }

  async checkPendingImport() {
    this.importSuppressed = false

    const currentUserId = store.getters['auth/userId']
    if (!currentUserId) {
      this.clearPersistedImport()
      return
    }

    this.currentUserId = currentUserId

    if (!isOnBookmarksPage()) {
      return
    }

    // Check if we have a pending import already set up (from import confirmation page)
    if (this.pendingImport && !this.isImportToastOpen.value && this.hasSessionProgress) {
      if (
        this.currentSessionImportId &&
        this.pendingImport.importId === this.currentSessionImportId
      ) {
        this.showImportToast()
        return
      }
    }

    this.hasSessionProgress = false

    // Check localStorage first for persistent import data
    let storedImport = this.getStoredImport()
    if (storedImport) {

      // Validate user context
      if (storedImport.userId && storedImport.userId !== currentUserId) {
        this.clearPersistedImport()
        storedImport = null
      }

      // Clear any fake imports from localStorage since they shouldn't persist
      // BUT keep completed imports - they just need to show the toast once
      if (isFakeImportId(storedImport?.importId) &&
          !['completed', 'failed', 'timeout'].includes(storedImport?.status)) {
        this.clearPersistedImport()
        storedImport = null
      }

      // Also clear imports older than 24 hours as they're likely stale
      if (storedImport) {
        const importAge = Date.now() - new Date(storedImport.startedAt || 0).getTime()
        const maxAge = 24 * 60 * 60 * 1000

        if (importAge > maxAge) {
          this.clearPersistedImport()
          storedImport = null
        }

        // Clear completed imports older than 5 minutes (TTL for completion toasts)
        if (storedImport && ['completed', 'failed', 'timeout'].includes(storedImport.status)) {
          const completedAt = storedImport.completedAt || storedImport.lastUpdated || storedImport.startedAt
          if (completedAt) {
            const completedAge = Date.now() - new Date(completedAt).getTime()
            const maxCompletedAge = 5 * 60 * 1000 // 5 minutes TTL for completed imports
            if (completedAge > maxCompletedAge) {
              this.clearPersistedImport()
              storedImport = null
            }
          }
        }

        // If stored import doesn't match current session import, it's from a previous session
        if (
          storedImport &&
          this.currentSessionImportId &&
          storedImport.importId !== this.currentSessionImportId
        ) {
          storedImport.isFromPreviousSession = true
        }
      }
    }

    let activeImport = null

    // Check stored import - handle completed imports differently than in-progress ones
    if (storedImport?.importId) {
      // If import is already marked as completed in localStorage, show it without API validation
      if (['completed', 'failed', 'timeout'].includes(storedImport.status)) {
        activeImport = storedImport
        this.hasSessionProgress = true
      } else {
        // For in-progress imports, validate with API
        const validation = await this.validateImportState(storedImport)

        if (validation.valid) {
          activeImport = {
            ...storedImport,
            ...validation.status
          }
        } else {
          this.clearPersistedImport()
        }
      }
    }

    // If no active import from storage, check for any processing imports
    if (!activeImport) {
      try {
        const history = await importsAPI.getImportHistory({ status: 'processing', limit: 1 })

        if (history?.imports?.length > 0) {
          const imp = history.imports[0]
          const validation = await this.validateImportState(imp)

          if (validation.valid) {
            const totalBookmarks =
              validation.status.totalBookmarks ||
              validation.status.numberOfBookmarks ||
              imp.totalBookmarks
            if (totalBookmarks >= this.minimumBookmarksForToast) {
              activeImport = {
                ...imp,
                ...validation.status
              }
              this.persistImport(activeImport)
            }
          }
        }
      } catch (historyError) {
        // Silently ignore errors fetching history
      }
    }

    // No active import confirmed by server - stay silent
    if (!activeImport) {
      this.clearPersistedImport()
      store.commit('collectionItems/clearActiveImport')
      store.commit('collectionItems/setImportQueueSize', 0)
      return
    }

    const normalized = this.normalizeProgress(activeImport)
    if (normalized.attempted > 0) {
      this.hasSessionProgress = true
    }

    // Set up import tracking with normalized data
    this.pendingImport = {
      ...activeImport,
      status: normalized.status,
      totalBookmarks: normalized.total,
      bookmarksProcessed: normalized.processed,
      bookmarksFailed: normalized.failed,
      lastUpdated: normalized.lastUpdated.toISOString(),
      lastError: normalized.lastError
    }

    // Also track in activeImports for multi-import aggregation
    if (activeImport.importId && !isFakeImportId(activeImport.importId)) {
      this.activeImports.set(activeImport.importId, this.pendingImport)
    }

    // Initialize toast props
    this.importToastProps.value = {
      showPercentage: true,
      showProgressCounter: CONFIG?.features?.showImportProgressCounter ?? true,
      isRunning: ['processing', 'pending'].includes(normalized.status),
      importQueueSize: normalized.pending,
      lastImportSize: normalized.total,
      progressPercentage: this.enforceMonotonicProgress(normalized.progressPercentage),
      status: normalized.status,
      totalBookmarks: normalized.total,
      bookmarksProcessed: normalized.processed,
      bookmarksFailed: normalized.failed,
      attempted: normalized.attempted,
      totalAttempted: normalized.attempted,
      totalQueueSize: normalized.pending,
      lastUpdated: normalized.lastUpdated,
      error: normalized.lastError,
      activeImportCount: this.getActiveImportCount() || 1
    }

    const isCompletedStatus = ['completed', 'failed', 'timeout'].includes(normalized.status)
    const shouldSkipWebSocket =
      !activeImport.importId ||
      isFakeImportId(activeImport.importId) ||
      isCompletedStatus

    if (shouldSkipWebSocket) {
      this.showImportToast()

      if (isCompletedStatus) {
        this.clearPersistedImport()
      } else {
        this.startProgressPolling()
      }

      return
    }

    // Start tracking via WebSocket first, wait for initial progress before showing toast
    if (importProgressWS && activeImport.importId) {
      try {
        importProgressWS.trackImport(activeImport.importId, {
          totalBookmarks: normalized.total,
          bookmarksProcessed: normalized.processed,
          bookmarksFailed: normalized.failed,
          progressPercentage: normalized.progressPercentage,
          lastUpdated: normalized.lastUpdated,
          pending: normalized.pending,
          attempted: normalized.attempted
        })

        // Set up a timeout to check WebSocket progress
        const checkWebSocketProgress = () => {
          const wsProgress = importProgressWS.state.imports[activeImport.importId]

          if (wsProgress) {
            const wsNormalized = this.normalizeProgress(wsProgress)
            const hasProgress = wsNormalized.attempted > 0 || wsNormalized.progressPercentage > 0
            const isCurrentSessionImport =
              !activeImport.isFromPreviousSession ||
              this.currentSessionImportId === activeImport.importId
            const shouldShow = (hasProgress || this.hasSessionProgress) && isCurrentSessionImport

            // Check if this is a stale completed import from a previous session
            if (wsNormalized.status === 'completed' && activeImport.isFromPreviousSession) {
              this.clearPersistedImport()
              store.commit('collectionItems/clearActiveImport')
              this.pendingImport = null
              this.importSuppressed = true
              return
            }

            if (shouldShow) {
              if (wsNormalized.attempted > 0) {
                this.hasSessionProgress = true
              }

              this.importToastProps.value = {
                ...this.importToastProps.value,
                isRunning: ['processing', 'pending'].includes(wsNormalized.status),
                progressPercentage: this.enforceMonotonicProgress(wsNormalized.progressPercentage),
                bookmarksProcessed: wsNormalized.processed,
                bookmarksFailed: wsNormalized.failed,
                status: wsNormalized.status,
                importQueueSize: wsNormalized.pending,
                totalQueueSize: wsNormalized.pending,
                attempted: wsNormalized.attempted,
                totalAttempted: wsNormalized.attempted,
                totalBookmarks: wsNormalized.total,
                lastUpdated: wsNormalized.lastUpdated,
                activeImportCount: this.getActiveImportCount() || 1
              }

              this.pendingImport = {
                ...this.pendingImport,
                status: wsNormalized.status,
                totalBookmarks: wsNormalized.total,
                bookmarksProcessed: wsNormalized.processed,
                bookmarksFailed: wsNormalized.failed,
                lastUpdated: wsNormalized.lastUpdated.toISOString(),
                lastError: wsNormalized.lastError
              }

              this.showImportToast()

              // If this is a completed import loaded from storage, clear it after showing
              // so it doesn't show again on next navigation
              if (['completed', 'failed', 'timeout'].includes(wsNormalized.status)) {
                this.clearPersistedImport()
              }
            } else {
              this.clearPersistedImport()
              store.commit('collectionItems/clearActiveImport')
              this.pendingImport = null
              this.importSuppressed = true
              return
            }
          } else if (importProgressWS.state.connected) {
            // WebSocket connected but no progress data - likely stale import
            this.clearPersistedImport()
            store.commit('collectionItems/clearActiveImport')
            this.pendingImport = null
            this.importSuppressed = true
            return
          }
        }

        // Check immediately if already connected
        if (importProgressWS.state.connected) {
          checkWebSocketProgress()
        } else {
          // Wait for connection then check
          const connectionCheckInterval = setInterval(() => {
            if (importProgressWS.state.connected) {
              clearInterval(connectionCheckInterval)
              setTimeout(checkWebSocketProgress, 500)
            }
          }, 100)

          // Timeout after 5 seconds - fall back to showing toast (unless suppressed)
          setTimeout(() => {
            clearInterval(connectionCheckInterval)
            if (!this.isImportToastOpen.value && !this.importSuppressed) {
              this.showImportToast()

              // If this is a completed import loaded from storage, clear it after showing
              if (['completed', 'failed', 'timeout'].includes(normalized.status)) {
                this.clearPersistedImport()
              }
            }
          }, 5000)
        }
      } catch (error) {
        console.error('[ImportToastManager] Failed to start WebSocket tracking:', error)
        this.showImportToast()

        // If this is a completed import loaded from storage, clear it after showing
        if (['completed', 'failed', 'timeout'].includes(normalized.status)) {
          this.clearPersistedImport()
        } else {
          this.startProgressPolling()
        }
      }
    } else {
      this.showImportToast()

      // If this is a completed import loaded from storage, clear it after showing
      if (['completed', 'failed', 'timeout'].includes(normalized.status)) {
        this.clearPersistedImport()
      } else {
        this.startProgressPolling()
      }
    }
  }

  startProgressPolling() {
    if (this.pollingInterval) {
      return
    }

    // If we don't have a pending import but queue has items, create a placeholder
    if (!this.pendingImport && store.getters['collectionItems/importQueueSize'] > 0) {
      const queueSize = store.getters['collectionItems/importQueueSize']

      if (queueSize < this.minimumBookmarksForToast) {
        return
      }

      const totalBookmarks = store.getters['collectionItems/lastImportSize'] || queueSize
      this.pendingImport = {
        importId: 'polling-' + Date.now(),
        totalBookmarks: totalBookmarks,
        startedAt: new Date().toISOString()
      }
    }

    // Define polling function
    const pollProgress = async () => {
      try {
        const trackedImportId =
          this.pendingImport && !isFakeImportId(this.pendingImport.importId)
            ? this.pendingImport.importId
            : null

        let apiStatus = null
        if (trackedImportId) {
          apiStatus = await importsAPI.getStatus(trackedImportId)
        }

        await store.dispatch('collectionItems/getStats')

        if (apiStatus) {
          const normalized = this.normalizeProgress(apiStatus)

          if (normalized.attempted > 0) {
            this.hasSessionProgress = true
          }

          this.importToastProps.value = {
            showPercentage: true,
            showProgressCounter: CONFIG?.features?.showImportProgressCounter ?? true,
            isRunning: ['processing', 'pending'].includes(normalized.status),
            importQueueSize: normalized.pending,
            lastImportSize: normalized.total,
            progressPercentage: this.enforceMonotonicProgress(normalized.progressPercentage),
            status: normalized.status,
            totalBookmarks: normalized.total,
            bookmarksProcessed: normalized.processed,
            bookmarksFailed: normalized.failed,
            attempted: normalized.attempted,
            totalAttempted: normalized.attempted,
            totalQueueSize: normalized.pending,
            lastUpdated: normalized.lastUpdated,
            error: normalized.lastError,
            activeImportCount: this.getActiveImportCount() || 1
          }

          this.pendingImport = {
            ...(this.pendingImport || {}),
            importId: trackedImportId,
            status: normalized.status,
            totalBookmarks: normalized.total,
            bookmarksProcessed: normalized.processed,
            bookmarksFailed: normalized.failed,
            lastUpdated: normalized.lastUpdated.toISOString(),
            lastError: normalized.lastError
          }

          if (!['processing', 'pending'].includes(normalized.status)) {
            clearInterval(this.pollingInterval)
            this.pollingInterval = null

            eventbus.emit('import:completed', {
              importId: trackedImportId,
              status: normalized.status,
              totalBookmarks: normalized.total,
              bookmarksProcessed: normalized.processed,
              bookmarksFailed: normalized.failed,
              progressPercentage: normalized.progressPercentage,
              progress: apiStatus,
              error: normalized.lastError
            })
            return
          }

          return
        }

        const queueSize = store.getters['collectionItems/importQueueSize']
        const lastImportSize =
          store.getters['collectionItems/lastImportSize'] ||
          this.importToastProps.value.totalBookmarks ||
          30

        if (queueSize === 0 || queueSize === null || queueSize === undefined) {
          if (!this.hasSessionProgress) {
            clearInterval(this.pollingInterval)
            this.pollingInterval = null
            this.pendingImport = null
            this.isImportToastOpen.value = false
            this.importToastInstance.value = null
            this.clearPersistedImport()
            store.commit('collectionItems/clearActiveImport')
            return
          }

          this.importToastProps.value = {
            ...this.importToastProps.value,
            isRunning: false,
            importQueueSize: 0,
            progressPercentage: 100,
            status: 'completed',
            bookmarksProcessed: lastImportSize,
            totalBookmarks: lastImportSize,
            activeImportCount: 0
          }

          clearInterval(this.pollingInterval)
          this.pollingInterval = null

          eventbus.emit('import:completed', {
            importId: this.pendingImport?.importId || 'polling',
            status: 'completed',
            totalBookmarks: lastImportSize,
            bookmarksProcessed: lastImportSize,
            bookmarksFailed: 0
          })

          setTimeout(() => {
            this.pendingImport = null
            this.isImportToastOpen.value = false
            this.importToastInstance.value = null
            store.commit('collectionItems/clearActiveImport')
            this.clearPersistedImport()
          }, 10000)
        } else {
          const totalBookmarks = this.importToastProps.value.totalBookmarks || lastImportSize || 30
          const processed = totalBookmarks - queueSize
          const attempted = Math.max(processed, 0)
          const percentage =
            totalBookmarks > 0 ? Math.floor((attempted / totalBookmarks) * 100) : 0

          if (attempted > (this.importToastProps.value.bookmarksProcessed || 0)) {
            this.hasSessionProgress = true
          }

          this.importToastProps.value = {
            showPercentage: true,
            showProgressCounter: CONFIG?.features?.showImportProgressCounter ?? true,
            isRunning: true,
            importQueueSize: queueSize,
            lastImportSize: totalBookmarks,
            progressPercentage: this.enforceMonotonicProgress(percentage),
            status: 'processing',
            totalBookmarks: totalBookmarks,
            bookmarksProcessed: attempted,
            bookmarksFailed: 0,
            attempted,
            totalAttempted: attempted,
            totalQueueSize: queueSize,
            activeImportCount: this.getActiveImportCount() || 1
          }
        }
      } catch (error) {
        console.error('[ImportToastManager] Polling error:', error)
      }
    }

    pollProgress()
    this.pollingInterval = setInterval(pollProgress, 2000)
  }

  /**
   * Ensures the displayed progress percentage never goes backwards.
   * @param {number} rawPercentage
   * @returns {number}
   */
  enforceMonotonicProgress(rawPercentage) {
    const clamped = Math.min(100, Math.max(0, rawPercentage || 0))
    if (clamped > this.maxPercentageSeen) {
      this.maxPercentageSeen = clamped
    }
    return this.maxPercentageSeen
  }

  normalizeProgress(progress = {}) {
    const derived = progress?.derived || {}
    const totalRaw =
      progress?.totalBookmarks ?? progress?.numberOfBookmarks ?? progress?.total ?? 0
    const total = Number.isFinite(Number(totalRaw)) ? Math.max(0, Number(totalRaw)) : 0
    const processedRaw = progress?.bookmarksProcessed ?? progress?.processed ?? 0
    const processed = Number.isFinite(Number(processedRaw)) ? Math.max(0, Number(processedRaw)) : 0
    const failedRaw = progress?.bookmarksFailed ?? progress?.failed ?? 0
    const failed = Number.isFinite(Number(failedRaw)) ? Math.max(0, Number(failedRaw)) : 0

    const attemptedCandidate = Number(derived.attempted ?? progress?.attempted)
    const attempted = Number.isFinite(attemptedCandidate)
      ? Math.max(0, attemptedCandidate)
      : processed + failed

    const pendingCandidate = Number(derived.pending ?? progress?.pending)
    const fallbackPending = Math.max(0, total - attempted)
    const pending = Number.isFinite(pendingCandidate)
      ? Math.max(0, pendingCandidate)
      : fallbackPending

    const normalizedCandidate = Number(
      derived.normalizedProgressPercentage ?? progress?.progressPercentage ?? progress?.percentage
    )
    const calculatedPercentage = total > 0 ? Math.round((attempted / total) * 100) : 0
    const progressPercentage = Number.isFinite(normalizedCandidate)
      ? Math.min(100, Math.max(0, normalizedCandidate))
      : Math.min(100, Math.max(0, calculatedPercentage))

    const lastUpdatedSource =
      progress?.lastServerUpdate ||
      progress?.lastUpdated ||
      progress?.lastProcessedAt ||
      progress?.completedAt ||
      progress?.updatedAt ||
      progress?.timestamp ||
      progress?.scrapeEndedAt ||
      progress?.startedAt ||
      progress?.createdAt ||
      null

    const lastUpdated = lastUpdatedSource ? new Date(lastUpdatedSource) : new Date()

    return {
      status: progress?.status || 'processing',
      total,
      processed,
      failed,
      attempted,
      pending,
      progressPercentage,
      lastUpdated,
      lastError: progress?.lastError || progress?.error
    }
  }

  /**
   * Get the count of active (non-terminal) imports being tracked
   * @returns {number}
   */
  getActiveImportCount() {
    let count = 0
    for (const imp of this.activeImports.values()) {
      if (!['completed', 'failed', 'timeout', 'cancelled'].includes(imp.status)) {
        count++
      }
    }
    return count
  }

  /**
   * Calculate aggregate display progress across all active imports and completed stats.
   * Used to show combined progress when multiple imports run in parallel.
   * @returns {{ total: number, processed: number, failed: number, attempted: number, pending: number, percentage: number }}
   */
  getDisplayProgress() {
    let total = this.completedStats.total
    let processed = this.completedStats.processed
    let failed = this.completedStats.failed
    let attempted = this.completedStats.processed + this.completedStats.failed
    let pending = 0

    for (const imp of this.activeImports.values()) {
      if (!['completed', 'failed', 'timeout', 'cancelled'].includes(imp.status)) {
        const impTotal = Number(imp.totalBookmarks || imp.total || 0)
        const impProcessed = Number(imp.bookmarksProcessed || imp.processed || 0)
        const impFailed = Number(imp.bookmarksFailed || imp.failed || 0)
        const impAttempted = Number.isFinite(Number(imp.attempted))
          ? Number(imp.attempted) : (impProcessed + impFailed)
        const impPending = Number.isFinite(Number(imp.pending))
          ? Number(imp.pending) : Math.max(0, impTotal - impAttempted)

        total += impTotal
        processed += impProcessed
        failed += impFailed
        attempted += impAttempted
        pending += impPending
      }
    }

    const percentage = total > 0 ? Math.round((attempted / total) * 100) : 0

    return { total, processed, failed, attempted, pending, percentage }
  }

  /**
   * Get stored import from localStorage
   * @returns {Object|null} Stored import data or null
   */
  getStoredImport() {
    try {
      const stored = localStorage.getItem('activeImport')
      return stored ? JSON.parse(stored) : null
    } catch (error) {
      console.error('[ImportToastManager] Failed to read stored import:', error)
      return null
    }
  }

  /**
   * Persist import data to localStorage with user context
   * @param {Object} importData - Import data to persist
   */
  persistImport(importData) {
    try {
      const userId = store.getters['auth/userId']
      if (!userId) {
        console.warn('[ImportToastManager] Cannot persist import without user ID')
        return
      }

      localStorage.setItem(
        'activeImport',
        JSON.stringify({
          importId: importData.importId,
          totalBookmarks: importData.totalBookmarks,
          startedAt: importData.startedAt || importData.createdAt || new Date().toISOString(),
          userId: userId,
          sessionId: this.currentSessionImportId,
          status: importData.status,
          bookmarksProcessed: importData.bookmarksProcessed,
          bookmarksFailed: importData.bookmarksFailed,
          completedAt: importData.completedAt
        })
      )
    } catch (error) {
      console.error('[ImportToastManager] Failed to persist import:', error)
    }
  }

  /**
   * Clear all import-related data from localStorage
   */
  clearPersistedImport() {
    try {
      localStorage.removeItem('activeImport')
      localStorage.removeItem('importQueueSize')
      localStorage.removeItem('lastImportSize')
    } catch (error) {
      console.error('[ImportToastManager] Failed to clear localStorage:', error)
    }
  }

  /**
   * Handle user logout - clean up all import-related state
   */
  handleUserLogout() {
    this.clearPersistedImport()
    this.pendingImport = null
    this.hasSessionProgress = false
    this.importSuppressed = false
    this.currentUserId = null
    this.currentSessionImportId = null
    this.recentlyCompletedImportIds.clear()
    this.activeImports.clear()
    this.completedStats = { total: 0, processed: 0, failed: 0 }
    this.maxPercentageSeen = 0

    if (this.isImportToastOpen.value && this.importToastInstance.value) {
      this.importToastInstance.value.close()
      this.isImportToastOpen.value = false
      this.importToastInstance.value = null
    }

    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }

    if (this.importToastTimer.value) {
      clearTimeout(this.importToastTimer.value)
      this.importToastTimer.value = null
    }

    if (importProgressWS) {
      importProgressWS.disconnect()
    }

    store.commit('collectionItems/clearActiveImport')
    store.commit('collectionItems/setImportQueueSize', 0)
  }

  /**
   * Handle user login - update user context
   */
  handleUserLogin(newUserId) {
    if (!newUserId) {
      newUserId = store.getters['auth/userId']
    }

    if (this.currentUserId && this.currentUserId !== newUserId) {
      this.handleUserLogout()
    }

    this.currentUserId = newUserId
    this.hasSessionProgress = false
    this.currentSessionImportId = null
    this.recentlyCompletedImportIds.clear()
  }

  /**
   * Validate import state to ensure it's valid and belongs to current user
   * @param {Object} importData - Import data to validate
   * @returns {Promise<{valid: boolean, reason?: string, status?: Object}>}
   */
  async validateImportState(importData) {
    if (!importData || !importData.importId) {
      return { valid: false, reason: 'no_import_data' }
    }

    if (isFakeImportId(importData.importId)) {
      return { valid: false, reason: 'fake_import' }
    }

    // Check age - imports older than 24 hours are considered stale
    const startedAt = importData.startedAt || importData.createdAt
    if (startedAt) {
      const age = Date.now() - new Date(startedAt).getTime()
      const maxAge = 24 * 60 * 60 * 1000

      if (age > maxAge) {
        return { valid: false, reason: 'too_old' }
      }
    }

    // Check if import has been inactive for too long (15 minutes)
    const lastActivity = importData.lastProcessedAt || importData.lastUpdated
    if (
      lastActivity &&
      !['completed', 'failed', 'cancelled', 'timeout'].includes(importData.status)
    ) {
      const timeSinceActivity = Date.now() - new Date(lastActivity).getTime()
      const maxInactiveTime = 15 * 60 * 1000

      if (timeSinceActivity > maxInactiveTime) {
        return { valid: false, reason: 'stale_import' }
      }
    }

    const currentUserId = store.getters['auth/userId']
    if (!currentUserId) {
      return { valid: false, reason: 'not_authenticated' }
    }

    if (importData.userId && importData.userId !== currentUserId) {
      return { valid: false, reason: 'wrong_user' }
    }

    // Verify with backend API
    try {
      const status = await importsAPI.getStatus(importData.importId)

      if (!status) {
        return { valid: false, reason: 'not_found' }
      }

      if (['completed', 'failed', 'cancelled'].includes(status.status)) {
        return { valid: false, reason: 'already_finished', status }
      }

      // Double-check age from server data
      const serverStartTime = status.createdAt || status.startedAt
      if (serverStartTime) {
        const serverAge = Date.now() - new Date(serverStartTime).getTime()
        const maxServerAge = 24 * 60 * 60 * 1000

        if (serverAge > maxServerAge) {
          return { valid: false, reason: 'too_old_server' }
        }
      }

      return { valid: true, status }
    } catch (error) {
      return { valid: false, reason: 'api_error', error }
    }
  }

  /**
   * Handle import-related errors with recovery attempts
   * @param {Error} error - The error that occurred
   * @param {string} importId - The import ID if available
   */
  async handleImportError(error, importId) {
    console.error('[ImportToastManager] Import error occurred:', error, { importId })

    // Try to get current status from API as recovery
    if (importId && !importId.startsWith('sync-') && !importId.startsWith('polling-')) {
      try {
        const status = await importsAPI.getStatus(importId)

        if (status) {
          const normalized = this.normalizeProgress(status)
          this.handleImportProgress({
            importId,
            progress: {
              ...status,
              ...normalized
            }
          })

          if (status.status === 'processing') {
            if (importProgressWS && !importProgressWS.state.connected) {
              importProgressWS.trackImport(importId, status).catch((wsError) => {
                console.error('[ImportToastManager] WebSocket reconnection failed:', wsError)
                this.startProgressPolling()
              })
            } else {
              this.startProgressPolling()
            }
          } else {
            this.handleImportCompleted({
              importId,
              status: status.status,
              progress: status,
              error: status.lastError
            })
          }

          return
        }
      } catch (apiError) {
        console.error('[ImportToastManager] Failed to recover import status:', apiError)
      }
    }

    // If we can't recover, check if other imports are still active
    if (this.isImportToastOpen.value) {
      // Remove the failed import from tracking
      if (importId) {
        this.activeImports.delete(importId)
      }

      const remainingActive = this.getActiveImportCount()

      if (remainingActive > 0) {
        // Other imports still running — switch to next active import, don't show error
        const nextActive = [...this.activeImports.values()].find(
          imp => !['completed', 'failed', 'timeout', 'cancelled'].includes(imp.status)
        )
        if (nextActive) {
          this.pendingImport = {
            importId: nextActive.importId,
            totalBookmarks: nextActive.totalBookmarks || nextActive.total,
            bookmarksProcessed: nextActive.bookmarksProcessed || nextActive.processed || 0,
            bookmarksFailed: nextActive.bookmarksFailed || nextActive.failed || 0,
            status: nextActive.status || 'processing',
            startedAt: nextActive.startedAt || new Date().toISOString(),
            lastUpdated: new Date().toISOString()
          }
          this.currentSessionImportId = nextActive.importId
        }

        const agg = this.getDisplayProgress()
        this.importToastProps.value = {
          ...this.importToastProps.value,
          isRunning: true,
          status: 'processing',
          progressPercentage: this.enforceMonotonicProgress(agg.percentage),
          totalBookmarks: agg.total,
          bookmarksProcessed: agg.processed,
          bookmarksFailed: agg.failed,
          activeImportCount: remainingActive
        }
      } else {
        // No other imports — reset completed stats and show error state
        this.completedStats = { total: 0, processed: 0, failed: 0 }
        this.importToastProps.value = {
          ...this.importToastProps.value,
          isRunning: false,
          status: 'error',
          error: error.message || 'An error occurred during import',
          activeImportCount: 0
        }

        setTimeout(() => {
          if (this.getActiveImportCount() === 0) {
            this.isImportToastOpen.value = false
            this.importToastInstance.value = null
            this.pendingImport = null
          }
        }, 10000)
      }
    }

    // Clear persisted state if error is unrecoverable
    if (error.message?.includes('not found') || error.message?.includes('not authorized')) {
      this.clearPersistedImport()
      store.commit('collectionItems/clearActiveImport')
    }
  }

  /**
   * Handle WebSocket connection failures with fallback to polling
   */
  handleWebSocketFailure(importId) {
    console.warn('[ImportToastManager] WebSocket connection failed, switching to polling fallback')

    if (this.pendingImport || importId) {
      this.startProgressPolling()
    }
  }

  destroy() {
    this.cleanupFunctions.forEach((cleanup) => {
      try {
        cleanup()
      } catch (error) {
        console.error('[ImportToastManager] Error during cleanup:', error)
      }
    })
    this.cleanupFunctions = []

    if (this.importToastTimer.value) {
      clearTimeout(this.importToastTimer.value)
      this.importToastTimer.value = null
    }

    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }

    if (importProgressWS) {
      try {
        importProgressWS.disconnect()
      } catch (error) {
        console.error('[ImportToastManager] Error disconnecting WebSocket:', error)
      }
    }

    this.activeImports.clear()
    this.recentlyCompletedImportIds.clear()
    this.completedStats = { total: 0, processed: 0, failed: 0 }
    this.maxPercentageSeen = 0
    this.initialized = false
  }
}

// Export singleton instance
export default new ImportToastManager()
